import { Component } from 'react'
import * as firebase from 'firebase'

export default class FirebaseStateComponent extends Component {
  constructor (props) {
    super(props)

    this.mounted = false
    this.debugBindings = false
    this.bindings = {}
    this.callbacks = {}
  }

  // PUBLIC METHODS

  addBinding (stateKey, firebaseReferenceUrl) {
    if (this.debugBindings) {
      console.log('addBinding(' + stateKey + ', ' + firebaseReferenceUrl + ')')
    }

    const hasBinding = this.bindings.hasOwnProperty(stateKey)

    // Check if this binding already exists
    if (hasBinding && this.bindings[stateKey] === firebaseReferenceUrl) {
      if (this.debugBindings) {
        console.log('Exact binding for \'' + stateKey + '\' already exists, skipping...')
      }

      return
    }

    // Unlisten first when listening to another firebase reference for this state key
    if (this.mounted && hasBinding) {
      if (this.debugBindings) {
        console.log('Binding for \'' + stateKey + '\' already exists with another reference, removing...')
      }

      this.unbindStateKeyFromFirebase(stateKey)
    }

    // Store the binding
    this.bindings[stateKey] = firebaseReferenceUrl

    if (this.mounted) {
      if (this.debugBindings) {
        console.log('Already mounted, binding \'' + stateKey + '\' immediately...')
      }

      // Already mounted, bind immediately
      this.bindStateKeyToFirebase(stateKey, firebaseReferenceUrl)
    } else {
      if (this.debugBindings) {
        console.log('Not mounted yet, waiting for componentDidMount...')
      }
    }
  }

  removeBinding (stateKey, newValue = null) {
    if (this.debugBindings) {
      console.log('removeBinding(' + stateKey + ')')
    }

    const hasBinding = this.bindings.hasOwnProperty(stateKey)

    // Unlisten first when listening to another firebase reference for this state key
    if (this.mounted && hasBinding) {
      this.unbindStateKeyFromFirebase(stateKey)
      this.setStateValueForKey(newValue, stateKey)
    }

    // Remove the binding
    delete this.bindings[stateKey]
  }

  willUpdateStateValueForKey(stateKey) {
    // To be overwritten
  }

  didUpdateStateValueForKey(stateKey) {
    // To be overwritten
  }

  // REACT COMPONENT CALLBACKS

  componentDidMount () {
    // Store that the component was mounted
    this.mounted = true

    if (this.debugBindings) {
      console.log('componentDidMount')
    }

    // Enable listening for all changes
    this.bindAllBindings()
  }

  componentWillUnmount () {
    if (this.debugBindings) {
      console.log('componentWillUnmount')
    }

    // Stop listening for changes
    this.unbindAllBindings()

    // Store that the component was unmounted
    this.mounted = false
  }

  // PRIVATE METHODS

  bindAllBindings () {
    // Make sure the loading property is updated
    if (this.props.loading) {
      this.setStateValueForKey(true, 'loading')
    }

    // The bindings variable contains {'state key' => 'firebase reference'} pairs
    var stateKeys = Object.keys(this.bindings)

    if (this.debugBindings) {
      console.log('Adding all bindings, keys: ' + JSON.stringify(stateKeys))
    }

    stateKeys.forEach(stateKey => {
      // Get the firebase reference URL
      var firebaseReferenceUrl = this.bindings[stateKey]

      // Bind value changes at the specified URL to the state
      this.bindStateKeyToFirebase(stateKey, firebaseReferenceUrl)
    })
  }

  bindStateKeyToFirebase (stateKey, firebaseReferenceUrl) {
    if (this.debugBindings) {
      console.log('Adding firebase binding: {' + stateKey + ': ' + JSON.stringify(firebaseReferenceUrl) + '}')
    }

    var firebaseReference = firebase.database().ref().child(firebaseReferenceUrl)
    var callback = firebaseReference.on('value', snap => {
      // Notify that the value will change
      this.willUpdateStateValueForKey(stateKey)

      // Update the changed value in state with the specified key
      this.setStateValueForKey(snap.val(), stateKey, function () {
        // Notify that the object changed
        this.didUpdateStateValueForKey(stateKey)
      })

      // Make sure the loading property is updated
      if (this.props.loading) {
        this.setStateValueForKey(false, 'loading')
      }
    })

    // Save the firebase reference and the callback for unlistening
    this.callbacks[stateKey] = {
      firebaseReference: firebaseReference,
      callback: callback
    }
  }

  unbindAllBindings () {
    // The callbacks variable contains {'state key' => {ref, callback}} pairs
    var stateKeys = Object.keys(this.callbacks)

    if (this.debugBindings) {
      console.log('Removing all bindings, keys: ' + JSON.stringify(stateKeys))
    }

    // Unbind all bindings
    stateKeys.forEach(stateKey => {
      this.unbindStateKeyFromFirebase(stateKey)
    })
  }

  unbindStateKeyFromFirebase (stateKey) {
    if (this.debugBindings) {
      console.log('Removing firebase binding for state key: ' + stateKey)
    }

    // Get the firebase reference and the callback
    var obj = this.callbacks[stateKey]
    var firebaseReference = obj.firebaseReference
    var callback = obj.callback

    // Unregister the firebase listener
    firebaseReference.off('value', callback)

    // Remove the callback
    delete this.callbacks[stateKey]
  }

  setStateValueForKey (value, key, callback = null) {
    if (!this.mounted) {
      console.warn('Trying to set {' + key + ': ' + JSON.stringify(value) + '} to state while unmounted!')
      return
    }

    if (this.debugBindings) {
      console.log('Setting {' + key + ': ' + JSON.stringify(value) + '} to state')
    }

    var state = this.state
    state[key] = value
    this.setState(state, callback)
  }
}



// WEBPACK FOOTER //
// ./src/common/FirebaseStateComponent.js